import { useEffect, useState } from 'react';
import { api, type PlanOverview, type RecurringCandidate, type PriceChange } from '../lib/api.js';
import { useApp } from '../store/app.js';
import { Money } from '../components/Money.js';
import { Icon, CATEGORY_STYLE, DEFAULT_CATEGORY_STYLE } from '../components/Icon.js';
import { Progress } from '../components/Progress.js';
import { Sheet } from '../components/Sheet.js';
import { Skeleton } from '../components/Skeleton.js';
import { formatMoney, formatPercent, dayHeading, monthName } from '../lib/format.js';
import { t } from '../lib/i18n.js';

/** Budget, recurring charges and what is coming. §28-30 §25-27 §15-16 */
export function Budget() {
  const { categoryName, categorySlug, toast, refreshDashboard } = useApp();
  const [overview, setOverview] = useState<PlanOverview | null>(null);
  const [detected, setDetected] = useState<{ candidates: RecurringCandidate[]; priceChanges: PriceChange[] } | null>(null);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setError(null);
    api.plan.overview().then(setOverview).catch(() => setError(t.errors.load));
    api.plan.detectRecurring().then(setDetected).catch(() => undefined);
  };
  useEffect(load, []);

  const confirmCandidate = async (candidate: RecurringCandidate) => {
    try {
      await api.plan.confirmRecurring({
        merchant: candidate.merchant,
        expectedAmount: candidate.expectedAmount,
        interval: candidate.interval,
        nextExpectedDate: candidate.nextExpectedDate,
        categoryId: candidate.categoryId,
        confidence: candidate.confidence,
      });
      toast('סומן כחיוב קבוע', { tone: 'positive' });
      load();
      void refreshDashboard();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    }
  };

  return (
    <>
      <header className="app-header">
        <div className="app-header__bar">
          <span />
          <div className="app-header__title">תקציב</div>
          <button className="header-btn" onClick={() => setEditing(true)} aria-label="עריכת תקציב">
            <Icon name="settings" size={20} />
          </button>
        </div>
        {overview && <p className="app-header__sub">{monthName(overview.month.start)}</p>}
      </header>

      <main className="screen stack">
        {error && <ErrorCard message={error} onRetry={load} />}
        {!overview && !error && <><Skeleton height={150} radius={16} /><Skeleton height={200} radius={16} /></>}

        {overview && (
          <div className="desktop-grid">
            <div className="stack">
              <GlobalBudgetCard overview={overview} onEdit={() => setEditing(true)} />

              {overview.categories.length > 0 && (
                <section className="card">
                  <div className="card-head"><h2>תקציב לפי קטגוריה</h2></div>
                  <div className="stack">
                    {overview.categories.map((usage) => (
                      <div key={usage.budgetId} className="stack" style={{ gap: 6 }}>
                        <div className="between">
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{categoryName(usage.categoryId)}</span>
                          <span className="label--tiny">
                            {formatMoney(usage.spent)} / {formatMoney(usage.limit)}
                          </span>
                        </div>
                        <Progress value={usage.usage ?? 0} tone={toneFor(usage.status)} label={categoryName(usage.categoryId)} />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {detected && detected.priceChanges.length > 0 && (
                <section className="card">
                  <div className="card-head"><h2>עליות מחיר</h2></div>
                  <div className="list">
                    {detected.priceChanges.map((change) => (
                      <div className="tx-row" key={change.merchant}>
                        <span className="tx-row__icon" style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}>
                          <Icon name="arrow-up" size={18} />
                        </span>
                        <span className="tx-row__body">
                          <span className="tx-row__name">{change.merchant}</span>
                          <span className="tx-row__meta">
                            מ־{formatMoney(Math.abs(change.previousAmount))} ל־{formatMoney(Math.abs(change.currentAmount))}
                          </span>
                        </span>
                        <span className="tx-row__amount money money--negative">
                          +{formatPercent(change.deltaRatio)}
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {detected && detected.candidates.length > 0 && (
                <section className="card">
                  <div className="card-head"><h2>נראה שאלו חיובים קבועים</h2></div>
                  <p className="label" style={{ marginBottom: 12 }}>
                    זיהינו חזרתיות. אשר כדי שנכלול אותם בחישוב מה פנוי לך.
                  </p>
                  <div className="stack">
                    {detected.candidates.slice(0, 6).map((candidate) => (
                      <div key={candidate.merchantNorm} className="between" style={{ gap: 10 }}>
                        <span className="grow">
                          <span className="tx-row__name">{candidate.merchant}</span>
                          <span className="tx-row__meta">
                            {intervalLabel(candidate.interval)} · {formatMoney(Math.abs(candidate.expectedAmount))} ·
                            ודאות {Math.round(candidate.confidence * 100)}%
                          </span>
                        </span>
                        <button className="chip" onClick={() => confirmCandidate(candidate)}>סמן כקבוע</button>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className="stack">
              <section className="card">
                <div className="card-head"><h2>חיובים קבועים</h2></div>
                {overview.recurring.length === 0 ? (
                  <p className="label">עדיין לא סומנו חיובים קבועים.</p>
                ) : (
                  <div className="list">
                    {overview.recurring.map((item) => {
                      const style = CATEGORY_STYLE[categorySlug(item.categoryId)] ?? DEFAULT_CATEGORY_STYLE;
                      return (
                        <div className="tx-row" key={item.id}>
                          <span className="tx-row__icon" style={{ background: style.soft, color: style.color }}>
                            <Icon name={style.icon} size={18} />
                          </span>
                          <span className="tx-row__body">
                            <span className="tx-row__name">{item.merchant}</span>
                            <span className="tx-row__meta">
                              {intervalLabel(item.interval)}
                              {item.nextExpectedDate ? ` · הבא ${item.nextExpectedDate}` : ''}
                            </span>
                          </span>
                          <span className="tx-row__amount money">
                            <Money value={item.amount} size="inherit" tone="inherit" />
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              <section className="card">
                <div className="card-head"><h2>בקרוב</h2></div>
                {overview.upcoming.items.length === 0 ? (
                  <p className="label">אין חיובים צפויים בחודש הקרוב.</p>
                ) : (
                  <>
                    <div className="list">
                      {overview.upcoming.items.slice(0, 12).map((item) => (
                        <div className="tx-row" key={item.id}>
                          <span className="tx-row__body">
                            <span className="tx-row__name">{item.name}</span>
                            <span className="tx-row__meta">{dayHeading(item.dueDate, overview.today)}</span>
                          </span>
                          <span className="tx-row__amount money">
                            <Money value={item.amount} size="inherit" tone="inherit" />
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="label" style={{ marginTop: 10 }}>
                      סה״כ {formatMoney(overview.upcoming.total)} בחודש הקרוב
                    </p>
                  </>
                )}
              </section>

              {overview.liabilities.length > 0 && (
                <section className="card">
                  <div className="card-head"><h2>חובות</h2></div>
                  <div className="list">
                    {overview.liabilities.map((liability) => (
                      <div className="tx-row" key={liability.id}>
                        <span className="tx-row__body">
                          <span className="tx-row__name">{liability.name}</span>
                          <span className="tx-row__meta">
                            {liability.projection.payoffDate
                              ? `סיום צפוי ${liability.projection.payoffDate}`
                              : 'התשלום אינו מכסה את הריבית'}
                          </span>
                        </span>
                        <span className="tx-row__amount money money--negative">
                          <Money value={-liability.balance} size="inherit" tone="inherit" />
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        )}
      </main>

      <BudgetSheet
        open={editing}
        current={overview?.global?.limit ?? overview?.monthlyBudgetPreference ?? null}
        onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); load(); void refreshDashboard(); }}
      />
    </>
  );
}

function GlobalBudgetCard({ overview, onEdit }: { overview: PlanOverview; onEdit: () => void }) {
  const usage = overview.global;

  if (!usage) {
    return (
      <section className="card empty">
        <h2 className="empty__title">כמה אתה רוצה להוציא בחודש?</h2>
        <p className="empty__body">
          מספר אחד מספיק כדי להתחיל. אפשר להוסיף תקציבים לקטגוריות בהמשך.
        </p>
        <button className="btn btn--primary" onClick={onEdit}>הגדרת תקציב חודשי</button>
      </section>
    );
  }

  return (
    <section className="card" aria-label="תקציב חודשי">
      <div className="card-head">
        <h2>תקציב חודשי</h2>
        <button className="card-link inline-target" onClick={onEdit}>{t.common.edit}</button>
      </div>
      <div className="between" style={{ alignItems: 'flex-end', marginBottom: 12 }}>
        <span style={{ fontSize: 27, fontWeight: 800, letterSpacing: '-0.02em' }}>
          <Money value={usage.remaining} size="inherit" tone={usage.remaining < 0 ? 'negative' : 'neutral'} />
        </span>
        <span className="label">נשארו מתוך {formatMoney(usage.limit)}</span>
      </div>
      <Progress value={usage.usage ?? 0} tone={toneFor(usage.status)} label="ניצול תקציב" />
      <div className="between" style={{ marginTop: 8 }}>
        <span className="label">{formatMoney(usage.spent)} נוצלו</span>
        <span className="label--tiny">{usage.daysElapsed}/{usage.daysTotal} ימים</span>
      </div>
      <p className="label" style={{ marginTop: 10 }}>
        בקצב הנוכחי תסיים את החודש ב־{formatMoney(usage.projectedSpend)}
        {usage.projectedOverBy > 0 ? ` — חריגה של ${formatMoney(usage.projectedOverBy)}` : ''}.
        {usage.projectedBreachDate && ` התקציב צפוי להיגמר ב־${usage.projectedBreachDate}.`}
      </p>
    </section>
  );
}

function BudgetSheet({
  open, current, onClose, onSaved,
}: { open: boolean; current: number | null; onClose: () => void; onSaved: () => void }) {
  const { toast, setPreferences } = useApp();
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setValue(current ? String(current / 100) : '');
  }, [open, current]);

  const save = async () => {
    const minor = Math.round(Number(value.replace(',', '.')) * 100);
    if (!Number.isFinite(minor) || minor <= 0) { toast('צריך סכום גדול מאפס', { tone: 'danger' }); return; }
    setSaving(true);
    try {
      await api.plan.setBudget({ categoryId: null, amount: minor });
      await setPreferences({ monthlyBudget: minor });
      onSaved();
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} title="תקציב חודשי" onClose={onClose}>
      <div className="stack">
        <p className="label">כמה אתה רוצה להוציא בחודש? אפשר לשנות בכל רגע.</p>
        <input
          className="input input--amount"
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value.replace(/[^\d.,]/g, ''))}
          aria-label="תקציב חודשי"
          autoFocus
        />
        <button className="btn btn--primary btn--block" onClick={save} disabled={saving}>
          {saving ? 'שומר…' : t.common.save}
        </button>
      </div>
    </Sheet>
  );
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="card empty">
      <p className="empty__body">{message}</p>
      <button className="btn" onClick={onRetry}>{t.common.retry}</button>
    </div>
  );
}

function toneFor(status: string): 'positive' | 'warning' | 'danger' | 'neutral' {
  if (status === 'over') return 'danger';
  if (status === 'projected_over' || status === 'watch') return 'warning';
  return 'positive';
}

export function intervalLabel(interval: string): string {
  switch (interval) {
    case 'weekly': return 'שבועי';
    case 'monthly': return 'חודשי';
    case 'quarterly': return 'רבעוני';
    case 'yearly': return 'שנתי';
    default: return interval;
  }
}
